import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Factory, X } from 'lucide-react';
import {
  getMachineManufacturers, createMachineManufacturer, updateMachineManufacturer,
  deactivateMachineManufacturer, getMachineManufacturerCategories,
  getMachineModels, createMachineModel, deactivateMachineModel,
} from '@/lib/api';

const COUNTRIES = [
  { code: 'DE', name: 'Deutschland' }, { code: 'US', name: 'USA' },
  { code: 'JP', name: 'Japan' }, { code: 'CH', name: 'Schweiz' },
  { code: 'IT', name: 'Italien' }, { code: 'NL', name: 'Niederlande' },
  { code: 'DK', name: 'Dänemark' }, { code: 'GB', name: 'Großbritannien' },
  { code: 'FR', name: 'Frankreich' }, { code: 'ES', name: 'Spanien' },
  { code: 'IL', name: 'Israel' }, { code: 'AT', name: 'Österreich' },
  { code: 'SE', name: 'Schweden' }, { code: 'FI', name: 'Finnland' },
  { code: 'CN', name: 'China' },
];

// --- Manufacturer Form ---
const ManufacturerForm = ({ manufacturer, allCategories, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: manufacturer?.name || '',
    aliases: manufacturer?.aliases?.join(', ') || '',
    categories: manufacturer?.categories || [],
    country: manufacturer?.country || '',
    active: manufacturer?.active ?? true,
    internal_notes: manufacturer?.internal_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const toggleCategory = (cat) => {
    setForm(p => ({
      ...p,
      categories: p.categories.includes(cat)
        ? p.categories.filter(c => c !== cat)
        : [...p.categories, cat],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('admin.common.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        aliases: form.aliases.split(',').map(a => a.trim()).filter(Boolean),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <Label>Hersteller-Name *</Label>
        <Input
          value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          placeholder="z.B. Heidelberg"
          data-testid="machine-mfr-name-input"
        />
      </div>
      <div>
        <Label>Aliasse / Alternativnamen</Label>
        <Input
          value={form.aliases}
          onChange={e => setForm(p => ({ ...p, aliases: e.target.value }))}
          placeholder="Kommagetrennt, z.B. Heidelberger, Heidelberg Druckmaschinen"
          data-testid="machine-mfr-aliases-input"
        />
      </div>
      <div>
        <Label>Maschinen-Kategorien</Label>
        <div className="flex flex-wrap gap-1 mt-1" data-testid="machine-mfr-cats">
          {allCategories.map(c => (
            <Badge
              key={c}
              variant={form.categories.includes(c) ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              onClick={() => toggleCategory(c)}
            >
              {c}
            </Badge>
          ))}
        </div>
      </div>
      <div>
        <Label>Herkunftsland</Label>
        <Select value={form.country} onValueChange={(v) => setForm(p => ({ ...p, country: v }))}>
          <SelectTrigger data-testid="machine-mfr-country-select"><SelectValue placeholder="Land wählen" /></SelectTrigger>
          <SelectContent>
            {COUNTRIES.map(c => (
              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label>Aktiv</Label>
        <Switch
          checked={form.active}
          onCheckedChange={v => setForm(p => ({ ...p, active: v }))}
          data-testid="machine-mfr-active-switch"
        />
      </div>
      <div>
        <Label>Interne Notizen (nur Admin)</Label>
        <Textarea
          value={form.internal_notes}
          onChange={e => setForm(p => ({ ...p, internal_notes: e.target.value }))}
          rows={2}
          data-testid="machine-mfr-notes-input"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Abbrechen</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="machine-mfr-save-btn">
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </DialogFooter>
    </div>
  );
};

// --- Machine Models Dialog ---
const ModelsDialog = ({ open, onOpenChange, manufacturer }) => {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadModels = useCallback(async () => {
    if (!manufacturer) return;
    setLoading(true);
    try {
      const res = await getMachineModels({ manufacturer_id: manufacturer.manufacturer_id, active_only: false });
      setModels(res.data || []);
    } catch {
      toast.error(t('admin.machMfr.loadModelsError'));
    } finally {
      setLoading(false);
    }
  }, [manufacturer]);

  useEffect(() => {
    if (open) loadModels();
  }, [open, loadModels]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createMachineModel({ manufacturer_id: manufacturer.manufacturer_id, model_name: newName.trim() });
      toast.success(t('admin.machMfr.modelAdded'));
      setNewName('');
      loadModels();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateMachineModel(id);
      toast.success(t('admin.vert.categoryDeactivated'));
      loadModels();
    } catch {
      toast.error(t('admin.machMfr.deactivateError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{manufacturer?.name}</DialogTitle>
          <DialogDescription>
            {t('admin.machMfr.autocompleteHint')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              data-testid="machine-model-new-input"
            />
            <Button onClick={handleAdd} disabled={saving || !newName.trim()} data-testid="machine-model-add-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('common.add')}
            </Button>
          </div>
          {loading ? (
            <div className="py-6 text-center text-muted-foreground">{t('admin.common.loading')}</div>
          ) : models.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm">{t('admin.machMfr.noModels')}</div>
          ) : (
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y" data-testid="machine-model-list">
              {models.map(m => (
                <div key={m.model_id} className="flex items-center justify-between px-3 py-2 hover:bg-muted/50">
                  <div>
                    <span className={`text-sm ${m.active ? '' : 'line-through text-muted-foreground'}`}>
                      {m.model_name}
                    </span>
                    {!m.active && <Badge variant="secondary" className="ml-2 text-xs">inaktiv</Badge>}
                  </div>
                  {m.active && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeactivate(m.model_id)}
                      data-testid={`machine-model-deactivate-${m.model_id}`}
                    >
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Main Tab ---
export const MachineManufacturerTab = () => {
  const { t } = useTranslation();
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, mfr: null });
  const [modelsDialog, setModelsDialog] = useState({ open: false, mfr: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, cRes] = await Promise.all([
        getMachineManufacturers({ active_only: filterActive, search: search || undefined }),
        getMachineManufacturerCategories(),
      ]);
      setManufacturers(mRes.data || []);
      setCategories(cRes.data || []);
    } catch {
      toast.error(t('admin.machMfr.loadManufacturersError'));
    } finally {
      setLoading(false);
    }
  }, [search, filterActive, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (data) => {
    try {
      if (editDialog.mfr) {
        await updateMachineManufacturer(editDialog.mfr.manufacturer_id, data);
        toast.success(t('admin.md.changeApproved'));
      } else {
        await createMachineManufacturer(data);
        toast.success(t('admin.vert.categoryCreated'));
      }
      setEditDialog({ open: false, mfr: null });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
      throw err;
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(t('admin.vert.confirmDelete', { name: '' }))) return;
    try {
      await deactivateMachineManufacturer(id);
      toast.success(t('admin.vert.categoryDeactivated'));
      load();
    } catch {
      toast.error(t('admin.machMfr.deactivateError'));
    }
  };

  return (
    <Card data-testid="machine-manufacturer-tab">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5 text-purple-500" />
              {t('admin.machMfr.mainTitle')}
            </CardTitle>
            <CardDescription>
              {t('admin.machMfr.mainDesc')}
            </CardDescription>
          </div>
          <Button
            onClick={() => setEditDialog({ open: true, mfr: null })}
            className="gap-1.5"
            data-testid="machine-mfr-add-btn"
          >
            <Plus className="h-4 w-4" /> {t('admin.machMfr.mfr')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <Input
            placeholder={t('admin.machMfr.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs"
            data-testid="machine-mfr-search"
          />
          <div className="flex items-center gap-2">
            <Switch
              checked={filterActive}
              onCheckedChange={setFilterActive}
              id="filter-active"
              data-testid="machine-mfr-filter-active"
            />
            <Label htmlFor="filter-active" className="text-sm cursor-pointer">{t('admin.machMfr.onlyActive')}</Label>
          </div>
          <div className="flex-1 text-right text-sm text-muted-foreground">
            {t('admin.machMfr.entries', { count: manufacturers.length })}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">{t('admin.common.loading')}</div>
        ) : (
          <Table data-testid="machine-mfr-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.machMfr.tName')}</TableHead>
                <TableHead>{t('admin.machMfr.tAliases')}</TableHead>
                <TableHead>{t('admin.machMfr.tCategories')}</TableHead>
                <TableHead>{t('admin.machMfr.tCountry')}</TableHead>
                <TableHead>{t('admin.machMfr.tStatus')}</TableHead>
                <TableHead className="text-right">{t('admin.machMfr.tActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {manufacturers.map(m => (
                <TableRow key={m.manufacturer_id} data-testid={`machine-mfr-row-${m.manufacturer_id}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(m.aliases || []).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {(m.categories || []).map(c => (
                        <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{m.country || '—'}</TableCell>
                  <TableCell>
                    {m.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t('admin.machMfr.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('admin.machMfr.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setModelsDialog({ open: true, mfr: m })}
                      data-testid={`machine-mfr-models-${m.manufacturer_id}`}
                      title={t('admin.machMfr.manageModels')}
                    >
                      {t('admin.machMfr.models')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditDialog({ open: true, mfr: m })}
                      data-testid={`machine-mfr-edit-${m.manufacturer_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {m.active && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeactivate(m.manufacturer_id)}
                        data-testid={`machine-mfr-deactivate-${m.manufacturer_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {manufacturers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('admin.machMfr.noManufacturers')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {/* Edit / Create Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog(s => ({ ...s, open }))}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editDialog.mfr ? t('admin.machMfr.editMfr') : t('admin.machMfr.newMfr')}
              </DialogTitle>
              <DialogDescription>
                {t('admin.machMfr.maintain')}
              </DialogDescription>
            </DialogHeader>
            <ManufacturerForm
              manufacturer={editDialog.mfr}
              allCategories={categories}
              onSave={handleSave}
              onCancel={() => setEditDialog({ open: false, mfr: null })}
            />
          </DialogContent>
        </Dialog>

        <ModelsDialog
          open={modelsDialog.open}
          onOpenChange={(open) => setModelsDialog(s => ({ ...s, open }))}
          manufacturer={modelsDialog.mfr}
        />
      </CardContent>
    </Card>
  );
};
